<template>
  <div id="printable" class="summary-container">
    <!-- Header -->
    <div class="header">
      <div class="facility-name">
        TUURU COTTOLENGO HEALTH CENTRE - AR2026259
      </div>
      <div class="facility-details">
        <div>Igembe Central</div>
        <div>P.O BOX 77 - Meru | Tel: 254254746758069</div>
        <div>Email: suore.tuuru@cottolengo.org</div>
      </div>
      <div class="register-title">MOH 706 - LABORATORY MONTHLY SUMMARY</div>
    </div>

    <!-- Month/Year and Generated By Bar -->
    <div class="info-bar">
      <div class="info-left">
        <div class="info-item">
          <span class="info-label">Month:</span>
          <select v-model="selectedMonth" class="month-select">
            <option value="January">January</option>
            <option value="February">February</option>
            <option value="March">March</option>
            <option value="April">April</option>
            <option value="May">May</option>
            <option value="June">June</option>
            <option value="July">July</option>
            <option value="August">August</option>
            <option value="September">September</option>
            <option value="October">October</option>
            <option value="November">November</option>
            <option value="December">December</option>
          </select>
        </div>
        <div class="info-item">
          <span class="info-label">Year:</span>
          <input
            type="number"
            v-model="selectedYear"
            class="year-input"
            min="2020"
            max="2030"
          />
        </div>
        <div class="info-item">
          <span class="info-label">Generated By:</span>
          <span class="info-value">{{ generatedBy }}</span>
        </div>
      </div>
      <button @click="printPage" class="btn-print">Print Summary</button>
    </div>

    <!-- Period Display for Print -->
    <div class="print-period">
      <strong>Reporting Period:</strong> {{ selectedMonth }} {{ selectedYear }}
    </div>

    <!-- Section 1: Overall Laboratory Statistics -->
    <div class="section">
      <div class="section-title">1. OVERALL LABORATORY STATISTICS</div>
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-label">Total Tests Performed</div>
          <div class="stat-value">{{ overall.totalTests }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Total Patients Served</div>
          <div class="stat-value">{{ overall.totalPatients }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Tests per Patient (Avg)</div>
          <div class="stat-value">{{ overall.testsPerPatient }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Turnaround Time (Days)</div>
          <div class="stat-value">{{ overall.turnaroundTime }}</div>
        </div>
      </div>
    </div>

    <!-- Section 2: Haematology Tests -->
    <div class="section">
      <div class="section-title">2. HAEMATOLOGY TESTS</div>
      <div class="table-wrapper">
        <table class="data-table">
          <thead>
            <tr>
              <th>Test Name</th>
              <th>Tests Done</th>
              <th>Normal Results</th>
              <th>Abnormal Results</th>
              <th>Positivity Rate (%)</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="test in haematology" :key="test.name">
              <td>{{ test.name }}</td>
              <td>{{ test.done }}</td>
              <td>{{ test.normal }}</td>
              <td>{{ test.abnormal }}</td>
              <td>{{ test.positivityRate }}%</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Section 3: Clinical Chemistry Tests -->
    <div class="section">
      <div class="section-title">3. CLINICAL CHEMISTRY TESTS</div>
      <div class="table-wrapper">
        <table class="data-table">
          <thead>
            <tr>
              <th>Test Name</th>
              <th>Tests Done</th>
              <th>Normal Results</th>
              <th>Abnormal Results</th>
              <th>Abnormality Rate (%)</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="test in chemistry" :key="test.name">
              <td>{{ test.name }}</td>
              <td>{{ test.done }}</td>
              <td>{{ test.normal }}</td>
              <td>{{ test.abnormal }}</td>
              <td>{{ test.abnormalityRate }}%</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Section 4: Microbiology Tests -->
    <div class="section">
      <div class="section-title">4. MICROBIOLOGY TESTS</div>
      <div class="table-wrapper">
        <table class="data-table">
          <thead>
            <tr>
              <th>Test Name</th>
              <th>Tests Done</th>
              <th>Positive</th>
              <th>Negative</th>
              <th>Positivity Rate (%)</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="test in microbiology" :key="test.name">
              <td>{{ test.name }}</td>
              <td>{{ test.done }}</td>
              <td>{{ test.positive }}</td>
              <td>{{ test.negative }}</td>
              <td>{{ test.positivityRate }}%</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Section 5: Parasitology Tests -->
    <div class="section">
      <div class="section-title">5. PARASITOLOGY TESTS</div>
      <div class="table-wrapper">
        <table class="data-table">
          <thead>
            <tr>
              <th>Test Name</th>
              <th>Tests Done</th>
              <th>Positive</th>
              <th>Negative</th>
              <th>Positivity Rate (%)</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="test in parasitology" :key="test.name">
              <td>{{ test.name }}</td>
              <td>{{ test.done }}</td>
              <td>{{ test.positive }}</td>
              <td>{{ test.negative }}</td>
              <td>{{ test.positivityRate }}%</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Section 6: Serology Tests -->
    <div class="section">
      <div class="section-title">6. SEROLOGY TESTS</div>
      <div class="table-wrapper">
        <table class="data-table">
          <thead>
            <tr>
              <th>Test Name</th>
              <th>Tests Done</th>
              <th>Reactive/Positive</th>
              <th>Non-Reactive/Negative</th>
              <th>Reactivity Rate (%)</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="test in serology" :key="test.name">
              <td>{{ test.name }}</td>
              <td>{{ test.done }}</td>
              <td>{{ test.reactive }}</td>
              <td>{{ test.nonReactive }}</td>
              <td>{{ test.reactivityRate }}%</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Section 7: Urinalysis -->
    <div class="section">
      <div class="section-title">7. URINALYSIS</div>
      <div class="table-wrapper">
        <table class="data-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Tests Done</th>
              <th>Abnormal Findings</th>
              <th>Abnormality Rate (%)</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="test in urinalysis" :key="test.name">
              <td>{{ test.name }}</td>
              <td>{{ test.done }}</td>
              <td>{{ test.abnormal }}</td>
              <td>{{ test.abnormalityRate }}%</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Section 8: Blood Transfusion Services -->
    <div class="section">
      <div class="section-title">8. BLOOD TRANSFUSION SERVICES</div>
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-label">Blood Units Cross-matched</div>
          <div class="stat-value">{{ bloodTransfusion.crossMatched }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Blood Units Transfused</div>
          <div class="stat-value">{{ bloodTransfusion.transfused }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Transfusion Reactions</div>
          <div class="stat-value">{{ bloodTransfusion.reactions }}</div>
        </div>
      </div>
    </div>

    <!-- Section 9: Quality Control Indicators -->
    <div class="section">
      <div class="section-title">9. QUALITY CONTROL INDICATORS</div>
      <div class="table-wrapper">
        <table class="data-table">
          <thead>
            <tr>
              <th>Indicator</th>
              <th>Performance (%)</th>
              <th>Target (%)</th>
              <th>Achieved</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="qc in qualityControl" :key="qc.name">
              <td>{{ qc.name }}</td>
              <td>{{ qc.performance }}%</td>
              <td>{{ qc.target }}%</td>
              <td class="text-center">
                <span v-if="qc.performance >= qc.target" class="yes-badge"
                  >Yes</span
                >
                <span v-else class="no-badge">No</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Section 10: EQA Participation -->
    <div class="section">
      <div class="section-title">10. EXTERNAL QUALITY ASSESSMENT (EQA)</div>
      <div class="stats-grid-2">
        <div class="stat-card">
          <div class="stat-label">EQA Schemes Enrolled</div>
          <div class="stat-value">{{ eqa.enrolled }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Samples Tested</div>
          <div class="stat-value">{{ eqa.samplesTested }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Satisfactory Results</div>
          <div class="stat-value">{{ eqa.satisfactory }}</div>
        </div>
        <div class="stat-card warning" v-if="eqa.unsatisfactory > 0">
          <div class="stat-label">Unsatisfactory Results</div>
          <div class="stat-value">{{ eqa.unsatisfactory }}</div>
        </div>
      </div>
    </div>

    <!-- Section 11: Top 10 Test Requests -->
    <div class="section">
      <div class="section-title">11. TOP 10 MOST REQUESTED TESTS</div>
      <div class="table-wrapper">
        <table class="data-table">
          <thead>
            <tr>
              <th>Rank</th>
              <th>Test Name</th>
              <th>Number of Requests</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(test, index) in topTests" :key="index">
              <td class="text-center">{{ index + 1 }}</td>
              <td>{{ test.name }}</td>
              <td class="text-center">{{ test.count }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "MOH706Summary",
  data() {
    return {
      selectedMonth: "April",
      selectedYear: 2026,
      generatedBy: "SR. JANE KAREGI MUNYUGI",

      overall: {
        totalTests: 1245,
        totalPatients: 892,
        testsPerPatient: 1.4,
        turnaroundTime: 0.8,
      },

      haematology: [
        {
          name: "Full Blood Count (FBC)",
          done: 342,
          normal: 210,
          abnormal: 132,
          positivityRate: 38.6,
        },
        {
          name: "Hb Level",
          done: 456,
          normal: 320,
          abnormal: 136,
          positivityRate: 29.8,
        },
        {
          name: "Blood Grouping",
          done: 98,
          normal: 98,
          abnormal: 0,
          positivityRate: 0,
        },
        {
          name: "Cross Matching",
          done: 45,
          normal: 45,
          abnormal: 0,
          positivityRate: 0,
        },
      ],

      chemistry: [
        {
          name: "Blood Glucose (RBS)",
          done: 289,
          normal: 210,
          abnormal: 79,
          abnormalityRate: 27.3,
        },
        {
          name: "Blood Glucose (FBS)",
          done: 98,
          normal: 72,
          abnormal: 26,
          abnormalityRate: 26.5,
        },
        {
          name: "Lipid Profile",
          done: 56,
          normal: 38,
          abnormal: 18,
          abnormalityRate: 32.1,
        },
        {
          name: "Creatinine",
          done: 67,
          normal: 52,
          abnormal: 15,
          abnormalityRate: 22.4,
        },
        {
          name: "ALT/AST",
          done: 45,
          normal: 35,
          abnormal: 10,
          abnormalityRate: 22.2,
        },
      ],

      microbiology: [
        {
          name: "Gram Stain",
          done: 89,
          positive: 34,
          negative: 55,
          positivityRate: 38.2,
        },
        {
          name: "Culture & Sensitivity",
          done: 45,
          positive: 28,
          negative: 17,
          positivityRate: 62.2,
        },
        {
          name: "AFB Microscopy",
          done: 23,
          positive: 4,
          negative: 19,
          positivityRate: 17.4,
        },
        {
          name: "CSF Analysis",
          done: 12,
          positive: 3,
          negative: 9,
          positivityRate: 25.0,
        },
      ],

      parasitology: [
        {
          name: "Malaria RDT",
          done: 423,
          positive: 98,
          negative: 325,
          positivityRate: 23.2,
        },
        {
          name: "Malaria Microscopy",
          done: 89,
          positive: 22,
          negative: 67,
          positivityRate: 24.7,
        },
        {
          name: "Stool Ova/Cysts",
          done: 67,
          positive: 18,
          negative: 49,
          positivityRate: 26.9,
        },
        {
          name: "Urine Microscopy",
          done: 134,
          positive: 42,
          negative: 92,
          positivityRate: 31.3,
        },
      ],

      serology: [
        {
          name: "HIV Rapid Test",
          done: 234,
          reactive: 28,
          nonReactive: 206,
          reactivityRate: 12.0,
        },
        {
          name: "HBsAg",
          done: 89,
          reactive: 8,
          nonReactive: 81,
          reactivityRate: 9.0,
        },
        {
          name: "HCV Antibody",
          done: 67,
          reactive: 5,
          nonReactive: 62,
          reactivityRate: 7.5,
        },
        {
          name: "Syphilis RPR",
          done: 112,
          reactive: 6,
          nonReactive: 106,
          reactivityRate: 5.4,
        },
        {
          name: "Pregnancy Test",
          done: 156,
          reactive: 42,
          nonReactive: 114,
          reactivityRate: 26.9,
        },
      ],

      urinalysis: [
        { name: "Proteinuria", done: 234, abnormal: 28, abnormalityRate: 12.0 },
        { name: "Glycosuria", done: 234, abnormal: 18, abnormalityRate: 7.7 },
        { name: "Leukocytes", done: 234, abnormal: 42, abnormalityRate: 17.9 },
        { name: "Nitrites", done: 234, abnormal: 35, abnormalityRate: 15.0 },
        { name: "Microscopy", done: 156, abnormal: 52, abnormalityRate: 33.3 },
      ],

      bloodTransfusion: {
        crossMatched: 45,
        transfused: 42,
        reactions: 1,
      },

      qualityControl: [
        { name: "Internal QC Performance", performance: 95.5, target: 95 },
        {
          name: "Equipment Calibration Compliance",
          performance: 100,
          target: 100,
        },
        { name: "Reagent Stock Management", performance: 92, target: 95 },
        { name: "Sample Rejection Rate", performance: 2.5, target: 5 },
      ],

      eqa: {
        enrolled: 4,
        samplesTested: 12,
        satisfactory: 11,
        unsatisfactory: 1,
      },

      topTests: [
        { name: "Malaria RDT", count: 423 },
        { name: "Hb Level", count: 456 },
        { name: "Full Blood Count (FBC)", count: 342 },
        { name: "Blood Glucose (RBS)", count: 289 },
        { name: "HIV Rapid Test", count: 234 },
        { name: "Urinalysis", count: 234 },
        { name: "Pregnancy Test", count: 156 },
        { name: "Urine Microscopy", count: 134 },
        { name: "Syphilis RPR", count: 112 },
        { name: "Blood Grouping", count: 98 },
      ],
    };
  },
  methods: {
    printPage() {
      window.print();
    },
  },
};
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.summary-container {
  background: white;
  min-height: 100vh;
  padding: 20px;
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
}

.header {
  text-align: center;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid #1a56db;
}

.facility-name {
  font-size: 20px;
  font-weight: bold;
  color: #1a56db;
  margin-bottom: 6px;
}

.facility-details {
  font-size: 11px;
  color: #4b5563;
  line-height: 1.4;
  margin-bottom: 10px;
}

.register-title {
  font-size: 14px;
  font-weight: bold;
  background: #f3f4f6;
  display: inline-block;
  padding: 5px 16px;
  border-radius: 20px;
  color: #1f2937;
}

.info-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 16px;
  padding: 12px 16px;
  background: #f9fafb;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
}

.info-left {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  align-items: center;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-label {
  font-size: 12px;
  font-weight: 600;
  color: #374151;
}

.info-value {
  font-size: 12px;
  color: #1f2937;
  font-weight: 500;
}

.month-select {
  padding: 6px 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 12px;
  background: white;
}

.year-input {
  padding: 6px 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 12px;
  width: 70px;
}

.btn-print {
  padding: 8px 20px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
}

.btn-print:hover {
  background: #2563eb;
}

.print-period {
  text-align: center;
  padding: 8px;
  margin-bottom: 20px;
  background: #fef3c7;
  border-left: 4px solid #f59e0b;
  font-size: 12px;
  color: #92400e;
  border-radius: 6px;
}

.section {
  margin-bottom: 28px;
}

.section-title {
  font-size: 14px;
  font-weight: bold;
  color: #1f2937;
  background: #e5e7eb;
  padding: 8px 12px;
  border-radius: 8px;
  margin-bottom: 12px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 10px;
}

.stats-grid-2 {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
}

.stat-card {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 10px;
  padding: 12px 16px;
  text-align: center;
}

.stat-card.warning {
  background: #fef3c7;
  border-color: #fde68a;
}

.stat-card.warning .stat-value {
  color: #d97706;
}

.stat-label {
  font-size: 11px;
  font-weight: 600;
  color: #166534;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.stat-card.warning .stat-label {
  color: #92400e;
}

.stat-value {
  font-size: 24px;
  font-weight: bold;
  color: #059669;
}

.table-wrapper {
  overflow-x: auto;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.data-table th {
  background: #1f2937;
  color: white;
  padding: 8px;
  text-align: center;
  border: 1px solid #374151;
}

.data-table td {
  border: 1px solid #d1d5db;
  padding: 8px;
  text-align: center;
}

.data-table td:first-child {
  text-align: left;
  font-weight: 500;
}

.data-table td.text-center {
  text-align: center;
}

.yes-badge {
  background: #d1fae5;
  color: #065f46;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 600;
  display: inline-block;
}

.no-badge {
  background: #fee2e2;
  color: #991b1b;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 600;
  display: inline-block;
}

@media print {
  @page {
    size: portrait;
    margin: 0.5cm;
  }

  .btn-print,
  .month-select,
  .year-input {
    display: none;
  }

  .info-bar {
    display: none;
  }

  .print-period {
    background: #fef3c7;
    margin: 10px 0;
    padding: 5px;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .summary-container {
    padding: 0.2cm;
  }

  .header {
    margin-bottom: 5px;
  }

  .facility-name {
    font-size: 14px;
  }

  .facility-details {
    font-size: 8px;
  }

  .register-title {
    font-size: 10px;
  }

  .section-title {
    font-size: 11px;
    padding: 4px 8px;
  }

  .stat-value {
    font-size: 18px;
  }

  .stat-label {
    font-size: 9px;
  }

  .data-table {
    font-size: 9px;
  }

  .data-table th,
  .data-table td {
    padding: 4px;
  }

  .stat-card {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
}
</style>
